/* eslint-disable no-undefined */
import React, { useState, useCallback, useEffect, createElement, cloneElement, Children, isValidElement } from 'react';
import PropTypes from 'prop-types';
import warning from 'warning';
import TabTemplate from './TabTemplate';
import * as colors from '@constants/colors';
import InkBar from './InkBar';
import './tabs.scss';

const Tabs = ({
  children,
  contentContainerClassName,
  contentContainerStyle,
  initialSelectedIndex = 0,
  inkBarStyle,
  onChange = () => {},
  onScroll,
  style,
  tabItemContainerStyle,
  tabItemWidth,
  tabTemplate,
  tabTemplateStyle,
  value,
  ...other
}) => {
  const getTabs = useCallback(() => {
    const tabs = [];
    Children.forEach(children, (tab) => {
      if (isValidElement(tab)) {
        tabs.push(tab);
      }
    });
    return tabs;
  }, [children]);

  const getTabCount = useCallback(() => getTabs().length, [getTabs]);

  const getValueLink = useCallback(() => ({
    value,
    requestChange: onChange
  }), [value, onChange]);

  const getSelectedIndex = useCallback(() => {
    const valueLink = getValueLink();
    let selectedIdx = -1;
    getTabs().forEach((tab, index) => {
      if (valueLink.value === tab.props.value) {
        selectedIdx = index;
      }
    });
    return selectedIdx;
  }, [getValueLink, getTabs]);

  // Initialize selected index
  const computeInitialIndex = () => {
    const valueLink = getValueLink();
    if (typeof valueLink.value !== 'undefined') {
      return getSelectedIndex();
    }
    return initialSelectedIndex < getTabCount() ? initialSelectedIndex : 0;
  };

  const [selectedIndex, setSelectedIndex] = useState(computeInitialIndex);

  // Update selected index when value prop changes
  useEffect(() => {
    const valueLink = getValueLink();
    if (typeof valueLink.value !== 'undefined') {
      setSelectedIndex(getSelectedIndex());
    }
  }, [value, getValueLink, getSelectedIndex]);

  const handleTabTouchTap = useCallback((tabValue, event, tab) => {
    const valueLink = getValueLink();
    const index = tab.props.index;

    if ((valueLink.value && valueLink.value !== tabValue) ||
      selectedIndex !== index) {
      valueLink.requestChange(tabValue, event, tab);
    }

    setSelectedIndex(index);

    if (tab.props.onActive) {
      tab.props.onActive(tab);
    }
  }, [getValueLink, selectedIndex]);

  const getSelected = useCallback((tab, index) => {
    const valueLink = getValueLink();
    return valueLink.value ? valueLink.value === tab.props.value :
      selectedIndex === index;
  }, [getValueLink, selectedIndex]);

  const valueLink = getValueLink();
  const tabValue = valueLink.value;
  const tabContent = [];
  const tabCount = getTabCount();
  const defaultWidth = tabCount > 0 ? 100 / tabCount : 100;
  const width = tabItemWidth || `${defaultWidth}%`;
  const widthNum = tabItemWidth ? parseInt(tabItemWidth.replace('px', ''), 10) : defaultWidth;

  const tabs = getTabs().map((tab, index) => {
    warning(tab.type && tab.type.muiName === 'Tab',
      `Material-UI: Tabs only accepts Tab Components as children.
      Found ${tab.type.muiName || tab.type} as child number ${index + 1} of Tabs`);

    warning(!tabValue || typeof tab.props.value !== 'undefined',
      `Material-UI: Tabs value prop has been passed, but Tab ${index}
      does not have a value prop. Needs value if Tabs is going
      to be a controlled component.`);

    tabContent.push(tab.props.children ?
      createElement(tabTemplate || TabTemplate, {
        key: index,
        selected: getSelected(tab, index),
        style: tabTemplateStyle
      }, tab.props.children) : undefined);

    return cloneElement(tab, {
      key: index,
      index,
      selected: getSelected(tab, index),
      width,
      onClick: handleTabTouchTap
    });
  });

  const realSelectedIndex = valueLink.value ? getSelectedIndex() : selectedIndex;

  const inkBar = realSelectedIndex !== -1 ? (
    <InkBar
      color={colors.secondaryColor}
      left={`${widthNum * realSelectedIndex}${tabItemWidth ? 'px' : '%'}`}
      width={width}
      style={inkBarStyle}
    />
  ) : null;

  const inkBarContainerWidth = tabItemContainerStyle ?
    tabItemContainerStyle.width : '100%';

  return (
    <div
      style={style}
      {...other}
      styleName="tabs-container"
    >
      <div
        style={tabItemContainerStyle}
        styleName="tabs-item-container"
      >
        {tabs}
      </div>
      <div
        style={{ width: inkBarContainerWidth }}
        styleName="tabs-ink-bar-container"
      >
        {inkBar}
      </div>
      <div
        className={contentContainerClassName}
        onScroll={onScroll}
        styleName="tabs-content-container"
        style={contentContainerStyle}
      >
        {tabContent}
      </div>
    </div>
  );
};

Tabs.propTypes = {
  /**
   * Should be used to pass `Tab` components.
   */
  children: PropTypes.node,
  /**
   * The css class name of the root element.
   */
  className: PropTypes.string,
  /**
   * The css class name of the content's container.
   */
  contentContainerClassName: PropTypes.string,
  /**
   * Override the inline-styles of the content's container.
   */
  contentContainerStyle: PropTypes.object,
  /**
   * Specify initial visible tab index.
   * If `initialSelectedIndex` is set but larger than the total amount of specified tabs,
   * `initialSelectedIndex` will revert back to default.
   * If `initialSelectedIndex` is set to any negative value, no tab will be selected intially.
   */
  initialSelectedIndex: PropTypes.number,
  /**
   * Override the inline-styles of the InkBar.
   */
  inkBarStyle: PropTypes.object,
  /**
   * Called when the selected value change.
   */
  onChange: PropTypes.func,
  /**
   * Callback for content onScroll event.
   */
  onScroll: PropTypes.func,
  /**
   * Override the inline-styles of the root element.
   */
  style: PropTypes.object,
  /**
   * Override the inline-styles of the tab-labels container.
   */
  tabItemContainerStyle: PropTypes.object,
  /**
   * Override the default tab label item and ink bar width.
   */
  tabItemWidth: PropTypes.string,
  /**
   * Override the default tab template used to wrap the content of each tab element.
   */
  tabTemplate: PropTypes.func,
  /**
   * Override the inline-styles of the tab template.
   */
  tabTemplateStyle: PropTypes.object,
  /**
   * Makes Tabs controllable and selects the tab whose value prop matches this prop.
   */
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
};

export default Tabs;
